import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Slider,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { deviceAPI } from '../../API/deviceAPI';
import ModalTemplate from './ModalTemplate';

const CANVAS_W = 840;
const CANVAS_H = 476;

const AutoCadAlignModal = ({
  open,
  onClose,
  deviceId,
  bgImage,
  heatmapData,
  selectedDevice,
  setBgImageVersion,
  setDevices,
}) => {
  const { t } = useTranslation('heatmap');

  const [autoCadImgSrc, setAutoCadImgSrc] = useState(null);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [opacity, setOpacity] = useState(0.5);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);
  const [autoCrop, setAutoCrop] = useState(true);

  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0, rectWidth: 1, rectHeight: 1 });

  // Reset modal state on open/close or device change
  useEffect(() => {
    if (!open) {
      setAutoCadImgSrc(null);
      setOffsetX(0);
      setOffsetY(0);
      setScale(1.0);
      setRotation(0);
      setFlipX(false);
      setFlipY(false);
      setAutoCrop(true);
      setIsUploading(false);
    }
  }, [open, deviceId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setAutoCadImgSrc(event.target.result);
      setOffsetX(0);
      setOffsetY(0);
      setScale(1.0);
      setRotation(0);
      setFlipX(false);
      setFlipY(false);
      setAutoCrop(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleMouseDown = (e) => {
    if (!autoCadImgSrc) return;
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX,
      offsetY,
      rectWidth: rect.width || 1,
      rectHeight: rect.height || 1,
    };
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !autoCadImgSrc) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    const newOffsetX = dragStart.current.offsetX + (dx / dragStart.current.rectWidth);
    const newOffsetY = dragStart.current.offsetY + (dy / dragStart.current.rectHeight);
    setOffsetX(Math.max(-1, Math.min(1, newOffsetX)));
    setOffsetY(Math.max(-1, Math.min(1, newOffsetY)));
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    if (!autoCadImgSrc) return;
    if (e.cancelable) e.preventDefault();
    const delta = e.deltaY < 0 ? 0.05 : -0.05;
    setScale(prev => Math.max(0.1, Math.min(3.0, prev + delta)));
  };

  const handleSaveAutoCad = () => {
    if (!deviceId || !autoCadImgSrc) return;
    setIsUploading(true);

    const img = new Image();
    img.src = autoCadImgSrc;
    img.onerror = () => {
      setIsUploading(false);
      toast.error(t('toast.saveAutoCadError'));
    };
    img.onload = () => {
      const targetAspectRatio = (heatmapData?.gridWidth && heatmapData?.gridHeight)
        ? (heatmapData.gridWidth / heatmapData.gridHeight)
        : (bgImage?.naturalWidth ? (bgImage.naturalWidth / bgImage.naturalHeight) : 1.7647); // 840/476

      const W = autoCrop ? (bgImage?.naturalWidth || img.naturalWidth || 640) : img.naturalWidth;
      const H = autoCrop ? Math.round(W / targetAspectRatio) : img.naturalHeight;

      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, W, H);

      if (autoCrop) {
        ctx.translate(W / 2, H / 2);
        ctx.translate(offsetX * W, offsetY * H);
        ctx.scale(flipX ? -scale : scale, flipY ? -scale : scale);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -W / 2, -H / 2, W, H);
      } else {
        ctx.drawImage(img, 0, 0, W, H);
      }

      canvas.toBlob(async (blob) => {
        if (!blob) {
          setIsUploading(false);
          toast.error(t('toast.saveAutoCadError'));
          return;
        }
        try {
          const res = await deviceAPI.uploadAutoCadImage(deviceId, blob);
          if (res?.error) {
            toast.error(res.description || t('toast.saveAutoCadError'));
          } else {
            toast.success(t('toast.saveAutoCadSuccess'));
            onClose();
            setBgImageVersion(prev => prev + 1);
            if (setDevices) {
              setDevices(prev => prev.map(d => (d.id === deviceId ? { ...d, hasAutoCadImage: true } : d)));
            }
          }
        } catch (err) {
          toast.error(err.message || t('toast.saveAutoCadError'));
        } finally {
          setIsUploading(false);
        }
      }, 'image/jpeg', 0.95);
    };
  };

  const getImageStyle = () => {
    const containerAspectRatio = CANVAS_W / CANVAS_H;
    const targetAspectRatio = (heatmapData?.gridWidth && heatmapData?.gridHeight)
      ? (heatmapData.gridWidth / heatmapData.gridHeight)
      : (bgImage ? (bgImage.naturalWidth / bgImage.naturalHeight) : containerAspectRatio);

    if (targetAspectRatio > containerAspectRatio) {
      const heightPercent = (containerAspectRatio / targetAspectRatio) * 100;
      const topPercent = (1 - containerAspectRatio / targetAspectRatio) * 50;
      return {
        left: 0,
        right: 0,
        height: `${heightPercent}%`,
        top: `${topPercent}%`,
      };
    }
    const widthPercent = (targetAspectRatio / containerAspectRatio) * 100;
    const leftPercent = (1 - targetAspectRatio / containerAspectRatio) * 50;
    return {
      top: 0,
      bottom: 0,
      width: `${widthPercent}%`,
      left: `${leftPercent}%`,
    };
  };

  const modalContent = (
    <Box>
      {autoCadImgSrc && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('autocad.dragTip')}
          </Typography>
          <Button
            variant="outlined"
            component="label"
            size="small"
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            startIcon={<UploadIcon />}
          >
            {t('autocad.chooseFile')}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
        </Stack>
      )}

      {autoCadImgSrc ? (
        <Box>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: `${CANVAS_W}/${CANVAS_H}`,
              bgcolor: '#051124',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 3,
            }}
          >
            <Box
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUpOrLeave}
              onMouseLeave={handleMouseUpOrLeave}
              onWheel={handleWheel}
              onDoubleClick={() => {
                setOffsetX(0);
                setOffsetY(0);
                setScale(1.0);
                setRotation(0);
                setFlipX(false);
                setFlipY(false);
              }}
              sx={{
                position: 'absolute',
                ...getImageStyle(),
                overflow: 'hidden',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
              }}
            >
              {bgImage ? (
                <img
                  src={bgImage.src}
                  alt="Original Live Snapshot"
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    userSelect: 'none',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'absolute',
                    color: 'rgba(255,255,255,0.4)',
                    bgcolor: '#0B1D35',
                  }}
                >
                  {t('autocad.noRealImage')}
                </Box>
              )}

              <img
                src={autoCadImgSrc}
                alt="AutoCAD Overlay"
                draggable={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: opacity,
                  transform: `translate(${offsetX * 100}%, ${offsetY * 100}%) scale(${flipX ? -scale : scale}, ${flipY ? -scale : scale}) rotate(${rotation}deg)`,
                  transformOrigin: 'center',
                  userSelect: 'none',
                }}
              />

              {autoCrop && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: '2.5px dashed #ff9800',
                    pointerEvents: 'none',
                    zIndex: 10,
                    boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.15)',
                  }}
                />
              )}
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                {t('autocad.offsetX')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Slider
                  value={offsetX}
                  min={-1}
                  max={1}
                  step={0.002}
                  onChange={(_, val) => setOffsetX(val)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={Math.round(offsetX * 100)}
                  onChange={e => {
                    const val = Math.max(-100, Math.min(100, Number(e.target.value)));
                    setOffsetX(val / 100);
                  }}
                  sx={{ width: 85 }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" color="text.secondary">%</Typography>
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                {t('autocad.offsetY')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Slider
                  value={offsetY}
                  min={-1}
                  max={1}
                  step={0.002}
                  onChange={(_, val) => setOffsetY(val)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={Math.round(offsetY * 100)}
                  onChange={e => {
                    const val = Math.max(-100, Math.min(100, Number(e.target.value)));
                    setOffsetY(val / 100);
                  }}
                  sx={{ width: 85 }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" color="text.secondary">%</Typography>
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                {t('autocad.scale')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Slider
                  value={scale}
                  min={0.1}
                  max={3}
                  step={0.01}
                  onChange={(_, val) => setScale(val)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={Math.round(scale * 100)}
                  onChange={e => {
                    const val = Math.max(10, Math.min(300, Number(e.target.value)));
                    setScale(val / 100);
                  }}
                  sx={{ width: 85 }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" color="text.secondary">%</Typography>
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                {t('autocad.rotation')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Slider
                  value={rotation}
                  min={-180}
                  max={180}
                  step={1}
                  onChange={(_, val) => setRotation(val)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={rotation}
                  onChange={e => {
                    const val = Math.max(-180, Math.min(180, Number(e.target.value)));
                    setRotation(val);
                  }}
                  sx={{ width: 85 }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" color="text.secondary">°</Typography>
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                {t('autocad.opacity')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Slider
                  value={opacity}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(_, val) => setOpacity(val)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={Math.round(opacity * 100)}
                  onChange={e => {
                    const val = Math.max(0, Math.min(100, Number(e.target.value)));
                    setOpacity(val / 100);
                  }}
                  sx={{ width: 85 }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" color="text.secondary">%</Typography>
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                {t('autocad.quickOptions')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoCrop}
                      onChange={e => setAutoCrop(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontSize: 13 }}>{t('autocad.cropToHeatmap')}</Typography>}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFlipX(prev => !prev)}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  Flip X
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFlipY(prev => !prev)}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  Flip Y
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => {
                    setOffsetX(0);
                    setOffsetY(0);
                    setScale(1.0);
                    setRotation(0);
                    setFlipX(false);
                    setFlipY(false);
                    setAutoCrop(true);
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 'auto' }}
                >
                  {t('autocad.reset')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('autocad.uploadSubtitle')}
          </Typography>
          <Box
            component="label"
            sx={{
              width: '100%',
              aspectRatio: `${CANVAS_W}/${CANVAS_H}`,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: '2px solid rgba(255, 255, 255, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              color: 'text.secondary',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'action.selected',
                borderStyle: 'dashed',
                borderColor: 'primary.main',
                color: 'primary.main',
              },
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
            <UploadIcon sx={{ fontSize: 48, opacity: 0.8, mb: 0.5 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('autocad.loadPrompt')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('autocad.uploadHint')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <ModalTemplate
      open={open}
      onClose={() => !isUploading && onClose()}
      title={selectedDevice?.hasAutoCadImage ? t('autocad.adjustPlan') : t('autocad.addPlan')}
      size="md"
      content={modalContent}
      customActions={
        <Stack direction="row" gap={1}>
          <Button
            onClick={onClose}
            disabled={isUploading}
            sx={{ textTransform: 'none' }}
          >
            {t('autocad.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAutoCad}
            disabled={!autoCadImgSrc || isUploading}
            sx={{ textTransform: 'none' }}
            startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('autocad.saveAlignment')}
          </Button>
        </Stack>
      }
    />
  );
};

AutoCadAlignModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  deviceId: PropTypes.string,
  bgImage: PropTypes.object,
  heatmapData: PropTypes.object,
  selectedDevice: PropTypes.object,
  setBgImageVersion: PropTypes.func.isRequired,
  setDevices: PropTypes.func.isRequired,
};

export default AutoCadAlignModal;
